import { useEffect, useRef } from "react";
import { BONE, createFlock, paintFlock, stepFlock } from "../lib/butterflyFlight.js";
import { useThemeVersion } from "../lib/theme.js";

// Origin's thinking state: the space an answer is about to fill becomes the
// sign-in page's field, drawn exactly as ButterflyField draws it (8x12
// character cells, 11px mono, the same density ramp and faint dots) in the
// theme's ink. The status is set in that grid where the answer's first line
// will start, and one swallowtail circles the field's center. When `leaving`
// turns true, the butterfly sinks back into the dots and `onLeft` is called.
const CELL_W = 8;
const CELL_H = 12;
const FONT_PX = 11;
const FOCAL = 1.4;
const FPS = 30;
const RAMP = " .,:;-~=+tox%X$8@";
const DOT_ALPHA = 0.075;
const EMERGE_S = 0.9;
const SINK_S = 0.5;
// Three columns in and two rows down: the answer's text column and first line.
// A long status wraps by words, three lines at most.
const STATUS_COL = 3;
const STATUS_ROW = 2;
const STATUS_LINES = 3;
// A pointer at rest draws the nearest butterfly into a slow circle around it
// (butterflyFlight's curious behavior). A resting point at the center keeps it
// inside a narrow field, where its usual turns would carry it past the edges.
const CENTER = { x: 0, y: 0.08, present: true, speed: 0, restS: Infinity };
// Reduced motion: one still frame, a few seconds into the circle.
const STILL_AT_S = 3;

export default function ThinkingField({ status = "", leaving = false, onLeft = null, className = "" }) {
    const ref = useRef(null);
    const leavingRef = useRef(leaving);
    const onLeftRef = useRef(onLeft);
    // Read each frame, so a status that changes (a count arriving) is redrawn
    // without the field starting over.
    const statusRef = useRef(status);
    const themeVersion = useThemeVersion();
    leavingRef.current = leaving;
    onLeftRef.current = onLeft;
    statusRef.current = status;

    useEffect(() => {
        const cvs = ref.current;
        const host = cvs?.parentElement;
        const ctx = cvs?.getContext("2d");
        const sample = document.createElement("canvas");
        const sg = sample.getContext("2d", { willReadFrequently: true });
        if (!host || !ctx || !sg) return;
        const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const styles = getComputedStyle(host);
        const font = styles.getPropertyValue("--mono").trim() || "monospace";
        const ink = cssRgb(styles.getPropertyValue("--ink").trim() || "#1b1813");
        const ground = cssRgb(styles.getPropertyValue("--panel").trim() || styles.getPropertyValue("--bg").trim() || "#fefbf6");

        let view = null, flock = null, glyphs = null, cols = 0, rows = 0, dpr = 1;
        let t = 0, shown = still ? 1 : 0, sinkFrom = null, frame = 0, last = 0, pending = 0, stopped = false;

        function level(e) {
            if (sinkFrom == null) return e;
            return Math.max(0, 1 - (performance.now() - sinkFrom) / 1000 / SINK_S);
        }

        function render() {
            const e = level(shown);
            const lines = statusLines(statusRef.current, cols - 2 * STATUS_COL).map((line) => [...line]);
            const gw = CELL_W * dpr, gh = CELL_H * dpr;
            ctx.clearRect(0, 0, cvs.width, cvs.height);
            ctx.font = `${FONT_PX * dpr}px ${font}`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = rgba(ink, DOT_ALPHA * Math.min(1, e * 1.6));
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) ctx.fillText(".", (c + 0.5) * gw, (r + 0.5) * gh);
            }
            sg.setTransform(1 / CELL_W, 0, 0, 1 / CELL_H, 0, 0);
            paintFlock(sg, flock, view, t);
            const cells = sg.getImageData(0, 0, cols, rows).data;
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    const line = lines[r - STATUS_ROW];
                    if (line && c >= STATUS_COL && c < STATUS_COL + line.length) continue;
                    const i = (r * cols + c) * 4;
                    // As on the sign-in page: brightness times coverage picks the character.
                    const v = (cells[i] / BONE[0]) * (cells[i + 3] / 255) * e;
                    const k = Math.min(RAMP.length - 1, Math.floor(Math.pow(v, 0.85) * RAMP.length));
                    if (k > 0) ctx.drawImage(glyphs, k * gw, 0, gw, gh, c * gw, r * gh, gw, gh);
                }
            }
            if (lines.length) {
                ctx.fillStyle = rgba(mix(ground, ink, 0.72), Math.min(1, e * 1.4));
                lines.forEach((line, k) => line.forEach((ch, i) => ctx.fillText(ch, (STATUS_COL + i + 0.5) * gw, (STATUS_ROW + k + 0.5) * gh)));
            }
            return e;
        }

        function resize() {
            dpr = window.devicePixelRatio || 1;
            const w = host.clientWidth, h = host.clientHeight;
            cols = Math.floor(w / CELL_W);
            rows = Math.floor(h / CELL_H);
            if (!cols || !rows) { view = null; return; }
            cvs.width = Math.round(w * dpr);
            cvs.height = Math.round(h * dpr);
            cvs.style.width = `${w}px`;
            cvs.style.height = `${h}px`;
            sample.width = cols;
            sample.height = rows;
            const f = w * FOCAL;
            view = { W: w, H: h, f, hwu: w / 2 / f, hhu: h / 2 / f };
            glyphs = glyphAtlas(font, dpr, ground, ink);
            if (!flock) {
                flock = createFlock(view);
                flock.butterflies.length = 1; // one butterfly, not the sign-in page's two
                if (still) for (; t < STILL_AT_S; t += 1 / FPS) stepFlock(flock, 1 / FPS, t, view, CENTER);
            }
            render();
        }

        function tick(now) {
            if (stopped || document.hidden) return;
            pending += Math.min(0.1, (now - (last || now)) / 1000);
            last = now;
            if (pending >= 1 / FPS && view) {
                const dt = Math.min(0.05, pending);
                stepFlock(flock, dt, t, view, CENTER);
                t += dt;
                shown = Math.min(1, shown + dt / EMERGE_S);
                if (leavingRef.current && sinkFrom == null) sinkFrom = performance.now();
                pending = 0;
                if (render() <= 0 && sinkFrom != null) {
                    stopped = true;
                    onLeftRef.current?.();
                    return;
                }
            }
            frame = requestAnimationFrame(tick);
        }

        function onVisibility() {
            cancelAnimationFrame(frame);
            last = 0;
            if (!document.hidden && !stopped) frame = requestAnimationFrame(tick);
        }

        const observer = new ResizeObserver(resize);
        observer.observe(host);
        resize();
        if (still) return () => observer.disconnect();
        document.addEventListener("visibilitychange", onVisibility);
        frame = requestAnimationFrame(tick);
        return () => {
            stopped = true;
            observer.disconnect();
            cancelAnimationFrame(frame);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [themeVersion]);

    // With reduced motion nothing sinks; leaving ends the state at once.
    useEffect(() => {
        if (leaving && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) onLeftRef.current?.();
    }, [leaving]);

    return (
        <div role="status" className={`pointer-events-none absolute inset-0 ${className}`} data-thinking-field="">
            <canvas ref={ref} aria-hidden="true" className="absolute left-0 top-0" />
            <span className="sr-only">{status}</span>
        </div>
    );
}

/** The status, wrapped by words to `width` cells, in at most three lines (the last cut with "..." when more remains). */
export function statusLines(text, width) {
    const w = Math.max(8, width);
    const lines = [];
    let line = "";
    for (const word of String(text || "").split(/\s+/).filter(Boolean)) {
        if (!line) line = word;
        else if (line.length + 1 + word.length <= w) line += ` ${word}`;
        else {
            lines.push(line);
            line = word;
        }
        while (line.length > w) {
            lines.push(line.slice(0, w));
            line = line.slice(w);
        }
    }
    if (line) lines.push(line);
    if (lines.length <= STATUS_LINES) return lines;
    const kept = lines.slice(0, STATUS_LINES);
    kept[STATUS_LINES - 1] = `${kept[STATUS_LINES - 1].slice(0, w - 3)}...`;
    return kept;
}

// One character per ramp step, from a light tone of the ink to the ink itself.
function glyphAtlas(font, dpr, ground, ink) {
    const gw = CELL_W * dpr, gh = CELL_H * dpr;
    const atlas = document.createElement("canvas");
    atlas.width = gw * RAMP.length;
    atlas.height = gh;
    const g = atlas.getContext("2d");
    g.font = `${FONT_PX * dpr}px ${font}`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    [...RAMP].forEach((ch, i) => {
        g.fillStyle = rgba(mix(ground, ink, 0.3 + (0.7 * i) / (RAMP.length - 1)), 1);
        g.fillText(ch, i * gw + gw / 2, gh / 2);
    });
    return atlas;
}

// A CSS color as [r, g, b], read back through a canvas so any syntax works.
function cssRgb(color) {
    const g = document.createElement("canvas").getContext("2d");
    if (!g) return [27, 24, 19];
    g.fillStyle = "#000";
    g.fillStyle = color;
    const v = g.fillStyle;
    if (v.startsWith("#")) return [1, 3, 5].map((i) => parseInt(v.slice(i, i + 2), 16));
    const m = v.match(/[\d.]+/g);
    return m ? m.slice(0, 3).map(Number) : [27, 24, 19];
}

function mix(a, b, k) { return a.map((v, i) => Math.round(v + (b[i] - v) * k)); }
function rgba([r, g, b], a) { return `rgba(${r},${g},${b},${a})`; }
